/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { IconButton } from '@fluentui/react/lib/Button';
import { IDetailsList } from '@fluentui/react/lib/DetailsList';
import { ScrollToMode } from '@fluentui/react/lib/List';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { Page } from 'src/core/components/Page';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { UnSubmittedChangesPrompt } from 'src/core/components/UnsubmittedChangesPrompt';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { noopUndefined } from 'src/core/utils/base';
import { routeTable } from 'src/core/utils/routeTable';
import { getFlattenMetadata } from 'src/qnaMaker/appShell/qnaMakerEditPage/qnaMakerFiltersUtils';
import { QnaMakerQnaPairsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerDetailsCommandBar } from 'src/qnaMaker/appShell/qnaMakerQuestionPairDetailsPage/components/QnaMakerDetailsCommandBar';
import { QnaMakerDetailsTable } from 'src/qnaMaker/appShell/qnaMakerQuestionPairDetailsPage/components/QnaMakerDetailsTable';
import { QnaMakerTestPane } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerTestPane';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { qnaMakerQnaStoreToQnaSwagger, qnaMakerQnaSwaggerToQnaStore } from 'src/qnaMaker/modules/handlers/qnas/qnaMakerAssetsMappers';
import { useQnaPairsActions } from 'src/qnaMaker/modules/hooks/useQnaPairsActions';

const messages = defineMessages({
    pageTitle: {
        id: 'qnaMaker.QnaMakerQuestionPairDetails.pageTitle',
        defaultMessage: 'Question pair details'
    },
    reduceIconButtonAriaLabel: {
        id: 'qnaMaker.QnaMakerQuestionPairDetails.reduceIconButtonAriaLabel',
        defaultMessage: 'back to edit questions page'
    },
    unSubmittedChangesWarningMsg: {
        id: 'qnaMaker.QnaMakerQuestionPairDetails.unSubmittedChangesWarningMsg1',
        defaultMessage:
            "You can't save changes since you have edits in progress. Review edits in progress and submit or discard them to proceed."
    },
    unsavedChangesWarningMsg: {
        id: 'qnaMaker.QnaMakerQuestionPairDetails.unsavedChangesWarningMsg',
        defaultMessage: 'You have unsaved changes. Click on Save changes in the command bar to save your recent work.'
    }
});

export const QnaMakerQuestionPairDetails = observer(() => {
    const { localize } = useLocalization();
    const history = useHistory();
    const { dispatcher, rootStore, uiStore: portalUiStore } = useGlobalState();
    const {
        name: currentProjectName,
        id: currentProjectId,
        sourcesStoreHouse: { items: sourceStores },
        qnasJsonData
    } = rootStore.currentQnaMakerProjectStore;

    const uiStore = useLocalStore<QnaMakerQnaPairsUiStore>(() => new QnaMakerQnaPairsUiStore(portalUiStore));

    const {
        savingState,
        isRichTextEnabled,
        contextTreeMap,
        isTestPaneOpen,
        isContextTreeCalculating,
        qnaPairsBeingEditedStore,
        displayedQnaPairStores,
        detailsQnaPairId
    } = uiStore;

    const qnasFetchingTracker = useProgressTracker();

    const { qnaPairId } = useParams<{ qnaPairId: string }>();

    const detailsQnaPair = displayedQnaPairStores.find(q => q.id === detailsQnaPairId);

    const isTableLoading = qnasFetchingTracker.isRunning || !detailsQnaPair || uiStore.savingState === 'inProgress';

    React.useEffect(() => {
        if (!detailsQnaPair) {
            dispatcher.dispatch('getQnaMakerAssets', { tracker: qnasFetchingTracker });
            dispatcher.dispatch('getQnaMakerSources', { tracker: qnasFetchingTracker });
        }
    }, [detailsQnaPairId]);

    React.useEffect(() => {
        if (qnasJsonData.length) {
            uiStore.setDisplayedQnaPairStores(qnasJsonData.map(qnaMakerQnaSwaggerToQnaStore));
            uiStore.setQnaPairsJson(qnasJsonData);
            uiStore.setDetailsQnaPairId(+qnaPairId);
        }
    }, [qnasJsonData]);

    const { saveChanges, editQnaPair } = useQnaPairsActions(uiStore);

    const location = useLocation<{ qnaId: string }>();
    const decodedQnaPairId = location.state?.qnaId;
    const tableRef = React.useRef<IDetailsList>();

    const [openedPromptModalId, setOpenedPromptModalId] = React.useState<number>(null);

    const handleSubmitEditQnaPair = (id: number, newData: Partial<QnaMakerQnaSwagger>) => {
        if (qnaPairsBeingEditedStore.hasPair(id)) {
            const dataToBeSubmitted = qnaPairsBeingEditedStore.getPair(id);
            editQnaPair(id, dataToBeSubmitted);
            qnaPairsBeingEditedStore.deletePair(id);
        } else {
            editQnaPair(id, newData);
        }
    };

    const handleQnaContextualNavigation = (qnaId: number, delay: number = 0) => {
        setTimeout(() => {
            tableRef.current.focusIndex(1, false, noopUndefined, ScrollToMode.top);
            (document.querySelector(`#qna_${qnaId} > div`) as HTMLElement)?.click();

            if (decodedQnaPairId) {
                setTimeout(() => {
                    history.replace({ ...location, state: { ...location.state, qnaId: undefined } });
                }, 500);
            }
        }, delay);
    };

    const handleEditQnaPairFromTest = (qnaId: number) => {
        uiStore.setIsTestPaneOpen(false);
        handleQnaContextualNavigation(qnaId, 500);
    };

    return (
        <>
            <UnsavedChangesPrompt isPageDirty={savingState === 'dirty'} promptPath={routeTable.qnaMaker.detailsPage(currentProjectName)} />
            <UnSubmittedChangesPrompt
                isPageDirty={qnaPairsBeingEditedStore.hasPairsInEditMode}
                promptPath={routeTable.qnaMaker.detailsPage(currentProjectName)}
            />

            <Page
                size="container-fluid"
                pageName="QnaMakerQuestionPairDetailsPage"
                title={localize(messages.pageTitle)}
                reactToZoomLevel={false}>
                <Stack verticalFill tokens={{ childrenGap: 10 }}>
                    <Stack horizontal horizontalAlign="space-between">
                        <EditableTitle title={localize(messages.pageTitle)} />
                        <IconButton
                            iconProps={{ iconName: 'BackToWindow' }}
                            styles={{ root: { marginTop: '2px' } }}
                            onClick={() => history.push(routeTable.qnaMaker.editPage(currentProjectId))}
                            ariaLabel={localize(messages.reduceIconButtonAriaLabel)}
                        />
                    </Stack>

                    <QnaMakerDetailsCommandBar
                        onSave={() => saveChanges(true)}
                        enableRichText={isRichTextEnabled}
                        isInDirtyState={savingState !== 'saved'}
                        optionalColumnsShown={portalUiStore.qnaMakerEditKbOptionalColumnsVisibility}
                        onTest={() => uiStore.setIsTestPaneOpen(!isTestPaneOpen)}
                        areEditActionsDisabled={uiStore.savingState === 'inProgress'}
                        isEditQnaPairOn={qnaPairsBeingEditedStore.hasPairsInEditMode}
                        onChangeEnableRichText={() => uiStore.toggleIsRichTextEnabled()}
                        onChangeOptionalColumnsShown={key => dispatcher.dispatch('toggleQnaMakerEditKbOptionalColumnsVisibility', { key })}
                        onEdit={() => qnaPairsBeingEditedStore.addPair(qnaMakerQnaStoreToQnaSwagger(detailsQnaPair))}
                    />

                    {qnaPairsBeingEditedStore.hasPairsInEditMode ? (
                        <>
                            <MessageBar
                                isMultiline
                                dismissButtonAriaLabel="Close"
                                styles={{ root: { width: '100%' } }}
                                messageBarType={MessageBarType.warning}>
                                {localize(messages.unSubmittedChangesWarningMsg)}
                            </MessageBar>
                            <Announced aria-live="assertive" message={localize(messages.unSubmittedChangesWarningMsg)} />
                        </>
                    ) : (
                        savingState === 'dirty' && (
                            <>
                                <MessageBar
                                    isMultiline={false}
                                    dismissButtonAriaLabel="Close"
                                    styles={{ root: { width: '100%' } }}
                                    messageBarType={MessageBarType.warning}>
                                    {localize(messages.unsavedChangesWarningMsg)}
                                </MessageBar>
                                <Announced aria-live="assertive" message={localize(messages.unsavedChangesWarningMsg)} />
                            </>
                        )
                    )}

                    <QnaMakerDetailsTable
                        componentRef={tableRef}
                        sourceStores={sourceStores}
                        qnaPairsJson={qnasJsonData}
                        isLoading={isTableLoading}
                        items={[detailsQnaPair]}
                        contextTreeMap={contextTreeMap}
                        isRichTextEnabled={isRichTextEnabled}
                        overallItemsCount={1}
                        openedPromptModalId={openedPromptModalId}
                        onSubmitEditQnaPair={handleSubmitEditQnaPair}
                        isContextTreeCalculating={isContextTreeCalculating}
                        qnaPairsBeingEditedStore={qnaPairsBeingEditedStore}
                        optionalColumnsVisibility={portalUiStore.qnaMakerEditKbOptionalColumnsVisibility}
                        handleQnaContextualNavigation={handleQnaContextualNavigation}
                        areQnaTableActionsDisabled={uiStore.savingState === 'inProgress'}
                        onDismissEditQnaPair={id => qnaPairsBeingEditedStore.deletePair(id)}
                        onOpenedPromptModalChange={setOpenedPromptModalId}
                    />

                    <QnaMakerTestPane
                        isOpen={uiStore.isTestPaneOpen}
                        onEditQnA={handleEditQnaPairFromTest}
                        onDismiss={() => {
                            uiStore.setIsTestPaneOpen(false);
                        }}
                        onAddQuestion={(answerId, questions) => {
                            editQnaPair(answerId, { questions });
                        }}
                        flattenMetadata={getFlattenMetadata(qnasJsonData)}
                    />
                </Stack>
            </Page>
        </>
    );
});
